import type { LocationIntegrationVendorType } from '@cnapse/shared/schemas.js';

export interface VendorCapabilities {
  label: string;
  defaultApiBaseUrl: string;
  credentialFields: Array<{ key: string; label: string; masked: boolean }>;
  /** Plain English hint for operators — expected credential key names */
  credentialHint: string;
  supportsImportWizard: boolean;
  supportsZoneImport: boolean;
  supportsImageImport: boolean;
  supportsTagAutoRegistration: boolean;
  supportsNorthRotation: boolean;
  coordinateSystem: 'meters-bottomleft' | 'pixels' | 'wgs84' | 'none';
  orgIdLabel?: string;
}

export const VENDOR_CAPABILITIES: Record<LocationIntegrationVendorType, VendorCapabilities> = {
  kontakt: {
    label: 'Kontakt.io',
    defaultApiBaseUrl: 'https://app.cloud.us.kontakt.io/',
    credentialFields: [{ key: 'apiKey', label: 'API Key', masked: true }],
    credentialHint: 'Use key name apiKey (Kontakt.io Api-Key header).',
    supportsImportWizard: true,
    supportsZoneImport: true,
    supportsImageImport: true,
    supportsTagAutoRegistration: false,
    supportsNorthRotation: true,
    coordinateSystem: 'meters-bottomleft',
  },
  pozyx: {
    label: 'Pozyx',
    defaultApiBaseUrl: 'https://cloud.pozyx.io',
    credentialFields: [{ key: 'apiKey', label: 'API Key', masked: true }],
    credentialHint: 'Use apiKey for Pozyx Cloud Bearer token; orgId should be your Building ID.',
    supportsImportWizard: false,
    supportsZoneImport: false,
    supportsImageImport: false,
    supportsTagAutoRegistration: false,
    supportsNorthRotation: false,
    coordinateSystem: 'meters-bottomleft',
    orgIdLabel: 'Building ID',
  },
  indooratlas: {
    label: 'IndoorAtlas',
    defaultApiBaseUrl: 'https://api.indooratlas.com',
    credentialFields: [
      { key: 'apiKey', label: 'API Key', masked: true },
      { key: 'apiSecret', label: 'API Secret', masked: true },
    ],
    credentialHint: 'Use apiKey and apiSecret as provided by IndoorAtlas.',
    supportsImportWizard: false,
    supportsZoneImport: false,
    supportsImageImport: false,
    supportsTagAutoRegistration: false,
    supportsNorthRotation: false,
    coordinateSystem: 'wgs84',
  },
};

/** Discrete poll intervals supported by the integration UI (stored as ms in API). */
export const POLL_INTERVAL_OPTIONS_MS = [60000, 120000, 300000, 600000, 900000, 1800000] as const;

export function pollIntervalLabel(ms: number): string {
  switch (ms) {
    case 60000:
      return '1 minute';
    case 120000:
      return '2 minutes';
    case 300000:
      return '5 minutes';
    case 600000:
      return '10 minutes';
    case 900000:
      return '15 minutes';
    case 1800000:
    return '30 minutes';
    default:
      return `${ms / 1000}s`;
  }
}

/** External cloud positioning (Kontakt, Pozyx, IndoorAtlas) — not Cnapse-managed UWB. */
export function isThirdPartyLocationIntegration(
  vendorType: LocationIntegrationVendorType | null | undefined,
): boolean {
  return vendorType === 'kontakt' || vendorType === 'pozyx' || vendorType === 'indooratlas';
}
